import { isFunction } from 'lodash-es'

import envRaw from './env-raw'
import StrictObj from './strict-obj'
import extractRawElm from './extract-raw-elm'

import { getElmSign } from './get-elm-str'
import { onEvent, offEvent, onEventPassive } from './on-event.js'

const KEYPRESS = 'keypress'
const MOUSEDOWN = 'mousedown'
const BUTTON_NODE = 'BUTTON'

const ENTER_KEYCODE = 13
const SPACE_KEYCODE = 32

const LEFT_BUTTON = 0

const index2uh = []
const index2mh = []
const index2fh = []
const index2pairs = []

export function onClickPassive($elm, eventName, userHandler) {
  return _onClick(true, $elm, eventName, userHandler)
}

export function onClick($elm, eventName, userHandler) {
  return _onClick(false, $elm, eventName, userHandler)
}

function _onClick(passive, $elm, eventName, userHandler) {
  if (isFunction(eventName)) {
    userHandler = eventName
    eventName = envRaw.clickEvent
  }

  const index = index2uh.indexOf(userHandler)
  const elm = $elm === window ? $elm : extractRawElm($elm)
  const onFunc = passive ? onEventPassive : onEvent

  if (index !== -1) {
    const pairIndex = findPairIndex(index2pairs[index], elm, eventName)
    if (pairIndex === -1) {
      const pairs = index2pairs[index]
      const mainHandler = index2mh[index]

      pairs.push(StrictObj({ elm, eventName }))
      onFunc(elm, eventName, mainHandler)

      if (needsFallback(elm, eventName)) {
        if (!index2fh[index]) {
          index2fh[index] = makeFallbackHandler(userHandler)
        }

        const fallbackHandler = index2fh[index]
        onFunc(elm, KEYPRESS, fallbackHandler)
      }
    }
  } else {
    const mainHandler = makeMainHandler(userHandler)
    index2uh.push(userHandler)
    index2mh.push(mainHandler)
    index2pairs.push([StrictObj({ elm, eventName })])
    onFunc(elm, eventName, mainHandler)

    if (needsFallback(elm, eventName)) {
      const fallbackHandler = makeFallbackHandler(userHandler)
      index2fh.push(fallbackHandler)
      onFunc(elm, KEYPRESS, fallbackHandler)
    } else {
      index2fh.push(null)
    }
  }

  return $elm
}

export function offClick($elm, eventName, userHandler) {
  if (isFunction(eventName)) {
    userHandler = eventName
    eventName = envRaw.clickEvent
  }

  const index = index2uh.indexOf(userHandler)
  const elm = $elm === window ? $elm : extractRawElm($elm)

  if (index !== -1) {
    const pairs = index2pairs[index]
    const mainHandler = index2mh[index]

    offEvent(elm, eventName, mainHandler)

    if (needsFallback(elm, eventName)) {
      const fallbackHandler = index2fh[index]
      offEvent(elm, KEYPRESS, fallbackHandler)
    }

    const pairIndex = findPairIndex(pairs, elm, eventName)
    if (pairIndex !== -1) {
      pairs.splice(pairIndex, 1)
    }

    if (pairs.length === 0) {
      index2uh.splice(index, 1)
      index2mh.splice(index, 1)
      index2fh.splice(index, 1)
      index2pairs.splice(index, 1)
    }
  }

  return $elm
}

function needsFallback(elm, eventName) {
  return elm.nodeName === BUTTON_NODE && eventName === MOUSEDOWN
}

function makeMainHandler(userHandler) {
  return function mainHandler(event) {
    if (event.button === LEFT_BUTTON) {
      if (envRaw.showsOnClickDebug) {
        const eventSign = event.type
        const targetSign = getElmSign(event.target)
        console.debug(`${Date.now()} !onClick (main) "${eventSign}" on "${targetSign}"`)
      }
      return userHandler(event)
    }
  }
}

function makeFallbackHandler(userHandler) {
  return function fallbackHandler(event) {
    if (event.keyCode === ENTER_KEYCODE || event.keyCode === SPACE_KEYCODE) {
      if (envRaw.showsOnClickDebug) {
        const eventSign = event.type
        const targetSign = getElmSign(event.target)
        console.debug(`${Date.now()} !onClick (fallback) "${eventSign}" on "${targetSign}"`)
      }
      return userHandler(event)
    }
  }
}

function findPairIndex(pairs, elm, eventName) {
  for (let index = 0, length = pairs.length; index !== length; index++) {
    const pair = pairs[index]
    if (pair.elm === elm && pair.eventName === eventName) {
      return index
    }
  }

  return -1
}
